import { MenuItem, Box } from "@mui/material";
import { Field, Form, Formik } from "formik";
import _ from "lodash";
import { ReactElement } from "react";
import * as Yup from "yup";

import {
  ActionKitIntegration,
  CampaignContactActionKitImportArgs,
} from "~frontend/__generated__/graphql";
import RouteLeavingGuard from "~frontend/components/RouteLeavingGuard";
import { FormikSelect } from "~frontend/components/forms/FormSelect";
import { FormikTextField } from "~frontend/components/forms/FormTextField";
import FormikErrorBanner from "~frontend/components/forms/FormikErrorBanner";
import theme from "~frontend/theme";

import ContactImportFormSubmitButtons from "../ContactImportFormSubmitButtons";

export default function ImportForm({
  onSubmit,
  availableActionKitIntegrations,
  initialValues,
  readOnly,
  resetContactSourceSettings,
  ngpVanCampaignIntegrationEnabled,
}: {
  onSubmit?: (values: any) => void;
  availableActionKitIntegrations: ActionKitIntegration[];
  initialValues: CampaignContactActionKitImportArgs;
  readOnly?: boolean;
  resetContactSourceSettings: (() => void) | null;
  ngpVanCampaignIntegrationEnabled: boolean;
}): ReactElement {
  if (!availableActionKitIntegrations.length) {
    return <div />;
  }

  const showIntegrationPicker = availableActionKitIntegrations.length != 1;

  return (
    <Formik
      initialValues={initialValues}
      validationSchema={Yup.object({
        integrationId: Yup.string().required("Required"),
        reportShortName: Yup.string().required("Required"),
        phoneNumberColumn: Yup.string().required("Required"),
        ...(ngpVanCampaignIntegrationEnabled
          ? { vanIdColumn: Yup.string().required("Required") }
          : {}),
      })}
      onSubmit={onSubmit || _.noop}
    >
      {({ dirty }) => (
        <Form>
          <RouteLeavingGuard when={dirty} />
          <FormikErrorBanner />
          {showIntegrationPicker ? (
            <FormikSelect
              label="Integration"
              name="integrationId"
              withNoneOption={false}
              fullWidth
            >
              {availableActionKitIntegrations.map(integration => (
                <MenuItem value={integration.id} key={integration.id}>
                  {integration.name}
                </MenuItem>
              ))}
            </FormikSelect>
          ) : (
            <Field type="hidden" name="integrationId" />
          )}
          <FormikTextField
            label="ActionKit Report ID"
            name="reportShortName"
            fullWidth={true}
            disabled={readOnly}
          />
          <FormikTextField
            label="Phone Number Column"
            name="phoneNumberColumn"
            placeholder="cell"
            fullWidth={true}
            bottomLabel="Enter the name of the column in your ActionKit Report that contains phone numbers"
            disabled={readOnly}
          />
          {ngpVanCampaignIntegrationEnabled && (
            <FormikTextField
              label="VAN ID Column"
              name="vanIdColumn"
              placeholder="VAN_ID"
              fullWidth={true}
              bottomLabel="Enter the name of the column in your ActionKit Report that contains VAN IDs"
              disabled={readOnly}
            />
          )}
          {onSubmit && (
            <Box
              sx={{
                display: "flex",
                justifyContent: "flex-end",
                paddingTop: theme.spacing(1),
              }}
            >
              <ContactImportFormSubmitButtons
                resetContactSourceSettings={resetContactSourceSettings}
              />
            </Box>
          )}
        </Form>
      )}
    </Formik>
  );
}
